import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { PortableText } from "@portabletext/react";
import { sanityClient, urlFor } from "../../sanityClient";
import LoadingSpinner from "./loadingAnimation";
import BlogVideoPlayer from "../components/BlogVideoPlayer";

const ContentWrapper = styled("div")({
  fontSize: "18px",
  lineHeight: "1.8",
  color: "#444",
  marginTop: "1.5rem",
  maxWidth: "100%",
  overflowWrap: "break-word",
  wordWrap: "break-word",
  "& p": {
    marginBottom: "1.2rem",
  },
  "& strong": {
    color: "#000",
  },
  "& img": {
    maxWidth: "100%",
    height: "auto",
  },
  "& h1, & h2, & h3": {
    color: "#1e1e1e",
    lineHeight: 1.35,
    marginBottom: "1rem",
    maxWidth: "100%",
  },
  "@media (max-width: 768px)": {
    fontSize: "16px",
    lineHeight: "1.7",
  },
});

const PostText = ({ post }) => {
  return (
    <article className="blog-detail__article">
      <h1 className="blog-detail__title">{post.title}</h1>
      <PortableText
        value={post.body}
        components={{
          types: {
            image: ({ value }) => (
              <img
                src={urlFor(value).width(800).url()}
                alt={value.alt || "Post image"}
                className="my-4 rounded-lg"
              />
            ),
            video: ({ value }) => <BlogVideoPlayer url={value.url} />,
          },
          marks: {
            strong: ({ children }) => (
              <strong className="font-bold">{children}</strong>
            ),
            em: ({ children }) => <em className="italic">{children}</em>,
          },
        }}
      />
    </article>
  );
};

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);

  useEffect(() => {
    const fetchBlogDetail = async () => {
      try {
        // GROQ query: fetch blog by slug
        const query = `*[_type == "post" && slug.current == $slug][0]{
          title,
          publishedAt,
          body,
          mainImage,
          thumbnail
        }`;

        const data = await sanityClient.fetch(query, { slug });
        setBlog(data);
      } catch (error) {
        console.error("Error fetching blog detail:", error);
      }
    };

    fetchBlogDetail();
  }, [slug]);

  if (!blog) {
    return (
      <Container>
        <div className="d-flex justify-content-center mt-5">
          <LoadingSpinner color="#FF3E1F" />
        </div>
      </Container>
    );
  }

  return (
    <div>
      <Container maxWidth="md">
        <Box
          sx={{
            marginTop: { xs: 3, md: 5 },
            px: { xs: 2, sm: 3, md: 4 },
            pb: { xs: 4, md: 6 },
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              color: "#777",
              fontStyle: "italic",
              marginBottom: 3,
              fontSize: { xs: "0.9rem", md: "1rem" },
            }}
          >
            Published on {new Date(blog?.publishedAt).toLocaleDateString()}
          </Typography>

          <ContentWrapper>
            <PostText post={blog} />
          </ContentWrapper>
        </Box>
      </Container>
    </div>
  );
};

export default BlogDetail;
